import React from "react";
import { Card, CardBody, Container, Row, Col, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
import AnalyticsNavigation from "./components/AnalyticsNavigation";
import config from "../../config";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TradingAnalytics = () => {
  const [tradingData, setTradingData] = React.useState(null);
  const [dailyVolumeData, setDailyVolumeData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authUser");

        // Fetch trading data
        const tradingResponse = await fetch(
          `${config.API_URL}/api/admin/analytics/trading`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Fetch daily volume data
        const dailyVolumeResponse = await fetch(
          `${config.API_URL}/api/admin/analytics/visualization/daily-volume`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!tradingResponse.ok || !dailyVolumeResponse.ok) {
          throw new Error("Failed to fetch trading analytics data");
        }

        const tradingDataJson = await tradingResponse.json();
        const dailyVolumeDataJson = await dailyVolumeResponse.json();

        if (tradingDataJson.success && dailyVolumeDataJson.success) {
          setTradingData(tradingDataJson.data);
          setDailyVolumeData(dailyVolumeDataJson.data);
        } else {
          throw new Error("Failed to fetch trading analytics data");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching trading analytics data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Prepare daily volume data for chart
  const dailyVolumeChartData = {
    labels: dailyVolumeData ? dailyVolumeData.map((day) => day.date) : [],
    datasets: [
      {
        label: "Trading Volume",
        data: dailyVolumeData ? dailyVolumeData.map((day) => day.volume) : [],
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
        fill: false,
      },
    ],
  };

  // Prepare transaction metrics chart data
  const transactionChartData = {
    labels: ["Deposits", "Trades"],
    datasets: [
      {
        label: "Transaction Count",
        data: tradingData
          ? [tradingData.totalDepositAmount, tradingData.totalTrades]
          : [],
        backgroundColor: ["rgba(75, 192, 192, 0.6)", "rgba(255, 159, 64, 0.6)"],
        borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 159, 64, 1)"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Analytics" breadcrumbItem="Trading Analytics" />
        <AnalyticsNavigation activeTab="trading" />

        {loading && (
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <Card>
            <CardBody>
              <div className="alert alert-danger mb-0">
                Error loading trading analytics data: {error}
              </div>
            </CardBody>
          </Card>
        )}

        {tradingData && dailyVolumeData && (
          <>
            <Row>
              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          ${tradingData.totalTradingVolume.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Trading Vol</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className="ri-exchange-dollar-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {tradingData.totalTrades.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Trades</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-success">
                          <i className="ri-exchange-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          ${tradingData.totalDepositAmount.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Deposits</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-info align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-info">
                          <i className="ri-arrow-down-circle-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          ${tradingData.totalDepositAmount.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Withdraws</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-warning">
                          <i className="ri-arrow-up-circle-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Daily Trading Volume</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Line
                        data={dailyVolumeChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Transaction Metrics</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Bar
                        data={transactionChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Trading Breakdown</h4>
                    <div className="table-responsive">
                      <Table className="table-striped mb-0">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Average Trade Size</td>
                            <td>${tradingData.avgTradingValue.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Average Daily Volume</td>
                            <td>
                              $
                              {tradingData.avgDailyVolume
                                ? tradingData.avgDailyVolume.toFixed(2)
                                : tradingData.dailyTradingVolume.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Total Deposits</td>
                            <td>{tradingData.totalDepositAmount}</td>
                          </tr>
                          <tr>
                            <td>Average Deposit Amount</td>
                            <td>
                              ${tradingData.avgWalletDepositAmount.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Total Withdrawals</td>
                            <td>{tradingData.totalDepositAmount}</td>
                          </tr>
                          <tr>
                            <td>Average Withdrawal Amount</td>
                            <td>
                              $
                              {tradingData.avgWalletDepositAmount
                                ? tradingData.avgWalletDepositAmount.toFixed(2)
                                : "0.00"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default TradingAnalytics;
